import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  ExclamationCircleOutlined,
  LockFilled,
  SearchOutlined,
  SettingOutlined,
  WarningTwoTone,
} from '@ant-design/icons';
import {
  ContextButton,
  ExportButton,
  IconContextButton,
  ScopeButton,
} from '@monorepo/shared-lib';
import {
  Divider,
  Popover,
  Row,
  Space,
  TableProps,
  Tooltip,
} from 'antd';
import ExceptionStepOne from 'assets/images/exception-step-1.svg';
import ExceptionStepTwo from 'assets/images/exception-step-2.svg';
import GenTaxImage from 'assets/images/gen-tax-img-cropped.svg';
import { ReactComponent as EllipseIcon } from 'assets/images/icons/ellipsesIcon.svg';
import { ReactComponent as IconClose } from 'assets/images/icons/iconClose.svg';
import { ReactComponent as IconExport } from 'assets/images/icons/iconExport.svg';
import { ReactComponent as IconSupport } from 'assets/images/icons/iconSupport.svg';
import { StyledCard } from 'components/Custom/card';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import { RoundInput } from 'components/Custom/inputs';
import {
  CardDescSpan,
  CardTitleLargeSpan,
  ColoredTag,
} from 'components/Custom/spans';
import {
  CUSTOMISE_COLUMNS_ICON_FONT_SIZE,
  CustomiseColumnsIconButtonWithTooltip,
  customiseColumnsIconButtonStyle,
} from 'components/Custom/customise-columns-icon-button';
import { ColumnCustomizationDrawer } from 'components/Custom/ColumnCustomizationDrawer';
import { ScopeTable } from 'components/Custom/table';
import { ScopeTableFilters } from 'components/Custom/ScopeTableFilters';
import { FilterSelectionDrawer } from 'components/MainComponent/FilterSelectionDrawer';
import { UpgradeMessage } from 'containers/UpgradeMessage';
import moment from 'moment';
import { PAGE_SIZE_EXCEPTIONS } from 'pages/vat/report';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Provider,
  QboSourceType,
  UpgradeMessageSource,
  XeroSourceType,
  ZohoSourceType,
} from 'store/api/enums';
import {
  turnDrawerOff,
  turnDrawerOn,
  turnModalOff,
  turnModalOn,
} from 'store/app/actions';
import {
  DataLogStatus,
  DrawerType,
  MenuSize,
  WindowType,
} from 'store/app/enums';
import { getDrawerData, getDrawerOpen } from 'store/app/selectors';
import { DrawerData, ModalData } from 'store/app/types';
import { getAuthUser } from 'store/auth/selectors';
import { Entity } from 'store/organisation/models';
import { getOrgOrganisations } from 'store/organisation/selectors';
import { exportToCsvRequest, getDataPageRequest } from 'store/vat/actions';
import { ExceptionsActionType } from 'store/vat/models/exceptionWrapper';
import {
  DataJournal,
  JournalType,
  VatReportData,
} from 'store/vat/models/vatReportData';
import { getCsvLoadingState } from 'store/vat/selectors';
import styled from 'styled-components';
import { useNumberFormat } from 'utils/numberFormat';
import { formatDisplayDate } from 'i18n/calendarLabels';
import {
  Preference,
  PreferenceActions,
  PreferenceSections,
} from 'utils/preferences';
import { formatDate } from './transactions/utils/transaction.utils';
import { PwaVatTransactionsCardList } from './transactions/components/PwaVatTransactionsCardList';
import { PwaVatTxnDetailSheet } from './transactions/components/PwaVatTxnDetailSheet';
import { getVatReportTransactionProviderLink } from 'utils/get-provider-link';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { usePwaBottomSheetDragClose } from 'utils/usePwaBottomSheetDragClose';
import { useOverlayBackHandler } from 'utils/useOverlayBackHandler';
import { TransactionTableWrapper } from './transactions/styles';

const ExceptionsWrapper = styled.div`
  .ant-dropdown-trigger {
    color: #ffffff;
    /* Brand primary; high contrast mode darkens via --color-primary */
    background-color: var(--color-primary, #41aaaf);
    width: 124px;
    height: 30px;
    border-radius: 50px;
    display: block;
    line-height: 2.25;
    font-weight: 400;
  }

  .ant-dropdown-trigger:hover {
    color: var(--color-text-dark, #042a2a);
    background-color: #ffbc3e;
    font-weight: 400;
  }

  .ant-dropdown-trigger[disabled] {
    /* AA ≥4.5:1 (antd default ~#b8b8b8 on #f5f5f5 fails at ~1.8:1) */
    color: var(--color-tag-disabled, #5a6a6a) !important;
    background-color: var(--color-surface, #f3f7f8) !important;
    border: 1px solid var(--color-border, #dbeae3);
    opacity: 1 !important;
  }

  /* Ignore-tax warning row: orange background and left bar before checkbox */
  .ignore-tax-warning-row > td {
    background: var(--color-status-warning-bg, #fff5e2) !important;
  }
  .ignore-tax-warning-row:hover > td {
    background: #ffefcc !important;
  }
  .ignore-tax-warning-row > td:first-child {
    position: relative;
  }
  .ignore-tax-warning-row > td:first-child::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 6px;
    background: #e99144 !important;
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }
  /* Reserve space for icon; icon hidden until row hover (no layout shift) */
  .ignore-tax-warning-icon-slot {
    flex-shrink: 0;
  }
  .ignore-tax-warning-icon {
    visibility: hidden;
  }
  .ant-table-row:hover .ignore-tax-warning-icon {
    visibility: visible;
  }
`;

const ExceptionsSelectionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 16px;
  margin: 8px 0 10px;
  padding: 10px 14px;
  background: var(--color-surface-selected, #e9f6f7);
  border: 1px solid var(--color-primary, #41aaaf);
  border-radius: 10px;
`;

const SelectionBarStat = styled.div`
  display: flex;
  align-items: center;
  font-size: 13px;
  color: var(--color-text-dark, #042a2a);
  white-space: nowrap;
`;

const SelectionBarDivider = styled.span`
  width: 1px;
  height: 16px;
  background: var(--color-border, #dbeae3);
  flex-shrink: 0;
`;

interface RenderExceptionsProps {
  transData?: DataJournal[];
  filteredBox?: string | null;
  openAdjustmentPanal: (
    selectedRowKeys: DataJournal[],
    setSelectedRowKey: (data: DataJournal[]) => void,
    allExceptionsSelected: boolean,
    setAllExceptionsSelected: (val: boolean) => void
  ) => void;
  addExceptionAction: (
    exceptionActionType: string,
    selectedRowKeys: DataJournal[],
    setSelectedRowKey: (data: DataJournal[]) => void,
    allExceptionsSelected: boolean,
    setAllExceptionsSelected: (val: boolean) => void,
    selectedAdjustmentPeriod?: string | null,
    selectedCaptureDate?: any
  ) => void;
  handleExceptions: boolean;
  handlePreferences: (
    preferenceSection: PreferenceSections,
    action: PreferenceActions,
    preference: Preference
  ) => any;
  providerName: string;
  showSideMenu: boolean;
  menuSize: MenuSize;
  total?: number;
  reportId: string;
  status?: string;
  vatReportData?: VatReportData;
  onLoadReport?: () => void;
  lockExceptions?: boolean;
  currentOrganisation?: Entity;
  providerShortCode?: string;
}

const EXCEPTIONS_TUTORIAL_HIDE = 'exceptions-tutorial-hide';

const isIgnoreTaxError = (record: DataJournal) =>
  record.error?.includes("mapped to 'Ignore'") ?? false;

export const RenderExceptions: React.FC<RenderExceptionsProps> = ({
  transData,
  filteredBox,
  openAdjustmentPanal,
  addExceptionAction,
  handleExceptions,
  handlePreferences,
  providerName,
  showSideMenu,
  total,
  reportId,
  status,
  vatReportData,
  onLoadReport,
  lockExceptions,
  currentOrganisation,
  providerShortCode,
}) => {
  const { t, i18n } = useTranslation();
  const canApplyExceptionActions = handleExceptions && !lockExceptions;
  const exceptionsPanelHide = handlePreferences(
    PreferenceSections.EXCEPTIONS,
    PreferenceActions.GET,
    {
      name: EXCEPTIONS_TUTORIAL_HIDE,
    }
  );

  const [source, setSource] = React.useState<DataJournal[]>();
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<DataJournal[]>(
    []
  );
  //const [selectedAllRowKeys, setSelectedAllRowKeys] = React.useState<
  //   DataJournal[]
  //>([]);

  const [showCard, setShowCard] = React.useState(!exceptionsPanelHide);
  const [customPagination] = React.useState(true);
  const [tableFiltersState, setTableFiltersState] = React.useState<
    Record<string, any>
  >({});
  const [searchFilterValue, setSearchFilterValue] = React.useState<string>();
  const [loadTransactionsClicked, setLoadTransactionsClicked] =
    React.useState<boolean>(false);
  const [contextMenuVisible, setContextMenuVisible] = React.useState<{
    [key: string]: boolean;
  }>({});
  const [allRowsSelected, setAllRowsSelected] = React.useState<boolean>(false);
  const [tableHasFiltersApplied, setTableHasFiltersApplied] =
    React.useState<boolean>(false);
  const [selectedPwaException, setSelectedPwaException] =
    React.useState<DataJournal | null>(null);

  const dispatch = useDispatch();
  const { formatNumber } = useNumberFormat();
  const currentUser = useSelector(getAuthUser);
  const organisations = useSelector(getOrgOrganisations);
  const pwaMobileNav = usePwaMobileNavEnabled();
  const pwaDetailDragZoneRef = React.useRef<HTMLDivElement>(null);
  const closePwaExceptionDetail = React.useCallback(() => {
    setSelectedPwaException(null);
  }, []);
  useOverlayBackHandler(
    Boolean(pwaMobileNav && selectedPwaException),
    closePwaExceptionDetail,
    'vat-exceptions-detail',
    pwaMobileNav
  );
  const pwaDetailDragClose = usePwaBottomSheetDragClose(
    pwaMobileNav && !!selectedPwaException,
    closePwaExceptionDetail,
    pwaDetailDragZoneRef
  );
  const formatCardAmount = React.useCallback(
    (value?: number) =>
      new Intl.NumberFormat(i18n.language, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value || 0),
    [i18n.language]
  );
  const formatCardText = React.useCallback(
    (value?: string | number | null) =>
      value === null || value === undefined || value === '' ? '-' : String(value),
    []
  );

  const csvLoading = useSelector(getCsvLoadingState);
  const drawerOpen = useSelector(getDrawerOpen);
  const drawerData = useSelector(getDrawerData);
  const exceptionsColumnsDrawerOpen =
    drawerOpen && drawerData?.type === DrawerType.CUSTOMIZE_COLUMNS;

  const handleUpgradeClick = () => {
    const modalData: ModalData = {
      title: '',
      type: WindowType.WARNING,
      component: (
        <UpgradeMessage
          source={UpgradeMessageSource.EXCEPTIONS}
          onClose={() => dispatch(turnModalOff())}
          userAccountType={currentUser?.accountType}
        />
      ),
      maskClosable: false,
    };
    dispatch(turnModalOn(modalData));
  };

  const renderSortIcon = ({ sortOrder }) => {
    if (sortOrder == 'ascend') {
      return <ArrowDownOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    } else {
      return <ArrowUpOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    }
  };

  const EXCEPTIONS_COLUMNS_CONFIG = 'exceptions-columns-config';
  const EXCEPTIONS_ALWAYS_FIXED = ['exceptionActionType']; // Actions must always be fixed at the end

  const getSavedPageSize = () => {
    const pageSizeSaved = handlePreferences(
      PreferenceSections.EXCEPTIONS,
      PreferenceActions.GET,
      {
        name: PAGE_SIZE_EXCEPTIONS,
      }
    );

    if (typeof pageSizeSaved === 'number' && Number.isFinite(pageSizeSaved)) {
      return pageSizeSaved;
    } else {
      return 10;
    }
  };

  const handleServerFilter = React.useCallback(
    (filters: Record<string, any>, pageSize?: number) => {
      dispatch(
        getDataPageRequest(
          reportId,
          1,
          pageSize ?? getSavedPageSize(),
          JournalType.EXCEPTION,
          'detailed',
          filters
        )
      );
    },
    [dispatch, reportId]
  );

  // Get saved column configuration preferences
  const getSavedColumnConfig = React.useCallback(() => {
    const savedConfig = handlePreferences(
      PreferenceSections.EXCEPTIONS,
      PreferenceActions.GET,
      {
        name: EXCEPTIONS_COLUMNS_CONFIG,
      }
    );

    // If no saved preferences, return null (all columns visible by default)
    if (!savedConfig || typeof savedConfig !== 'object') {
      return null;
    }
    return savedConfig;
  }, [handlePreferences]);

  const savedConfig = getSavedColumnConfig();

  // Default visible columns for exceptions (Entity column off by default in drawer).
  // For Excel / Receipts providers we hide "accountCode" entirely so it doesn't
  // appear in the table or customization.
  const defaultVisibleColumnsExceptions = React.useMemo(() => {
    const base = [
      'journalCreatedDate',
      'taxName',
      'sourceType',
      'accountCode',
      'accountType',
      'accountName',
      'contact',
      'invoiceNumber',
      'description',
      'reference',
      'currency',
      'netAmountConverted',
      'taxAmountConverted',
      'exceptionActionType',
    ];
    const provider = providerName?.toLowerCase();
    if (provider === 'excel' || provider === 'receipts') {
      return base.filter((col) => col !== 'accountCode');
    }
    return base;
  }, [providerName]);

  const [visibleColumns, setVisibleColumns] = React.useState<string[] | null>(
    savedConfig?.visibleColumns || defaultVisibleColumnsExceptions
  );
  const [lockedColumns, setLockedColumns] = React.useState<string[]>(
    savedConfig?.lockedColumns || []
  );
  const [columnOrder, setColumnOrder] = React.useState<string[] | undefined>(
    savedConfig?.columnOrder
  );

  // Save column configuration preferences
  const handleColumnsChange = React.useCallback(
    (config: {
      visibleColumns: string[];
      lockedColumns: string[];
      columnOrder: string[];
    }) => {
      setVisibleColumns(config.visibleColumns);
      setLockedColumns(config.lockedColumns);
      setColumnOrder(config.columnOrder);
      handlePreferences(PreferenceSections.EXCEPTIONS, PreferenceActions.SET, {
        name: EXCEPTIONS_COLUMNS_CONFIG,
        value: config,
      });
    },
    [handlePreferences]
  );

  const resolveEntityForRow = React.useCallback(
    (journal: DataJournal) => {
      const isGroupContext =
        currentOrganisation?.providerName?.toLowerCase() ===
        Provider.GROUP.toLowerCase();
      if (isGroupContext) {
        const appConnectionId =
          (journal as any).applicationConnectionId ||
          (journal as any).applicationConnectionID;
        const orgByConnection = organisations?.find(
          (org) => org.uuid === appConnectionId
        );
        const groupResolvedName =
          orgByConnection?.friendlyName || orgByConnection?.legalName;
        if (groupResolvedName) {
          return groupResolvedName;
        }
        return (journal as any).entity ?? (journal as any).entityName;
      }
      return (
        currentOrganisation?.organisation ??
        (journal as any).entity ??
        (journal as any).entityName
      );
    },
    [
      currentOrganisation?.organisation,
      currentOrganisation?.providerName,
      organisations,
    ]
  );

  const resolveEntityProviderForRow = React.useCallback(
    (journal: DataJournal) => {
      const isGroupContext =
        currentOrganisation?.providerName?.toLowerCase() ===
        Provider.GROUP.toLowerCase();
      if (isGroupContext) {
        const appConnectionId =
          (journal as any).applicationConnectionId ||
          (journal as any).applicationConnectionID;
        const orgByConnection = organisations?.find(
          (org) => org.uuid === appConnectionId
        );
        return orgByConnection?.providerName ?? providerName;
      }
      return providerName;
    },
    [currentOrganisation?.providerName, organisations, providerName]
  );

  React.useEffect(() => {
    const enriched = transData?.map((j) => ({
      ...j,
      entityName: resolveEntityForRow(j),
      entity: resolveEntityForRow(j),
      entityProviderName: resolveEntityProviderForRow(j),
    }));
    setSource(enriched ?? transData);
  }, [transData, resolveEntityForRow, resolveEntityProviderForRow]);

  const getSelectableRows = React.useCallback(
    (rows?: DataJournal[]) =>
      (rows ?? []).filter((row) => !isIgnoreTaxError(row)),
    []
  );

  React.useEffect(() => {
    if (!allRowsSelected || !source) {
      return;
    }
    setSelectedRowKeys(getSelectableRows(source));
  }, [allRowsSelected, source, getSelectableRows]);

  React.useEffect(() => {
    if (tableHasFiltersApplied && allRowsSelected) {
      setAllRowsSelected(false);
    }
  }, [tableHasFiltersApplied, allRowsSelected]);

  const renderContextMenu = (record: DataJournal, onClose?: () => void) => {
    return (
      <div className="context-menu-items">
        <Space direction="vertical" style={{ rowGap: '0' }}>
          <IconContextButton
            type="text"
            onClick={() => {
              if (lockExceptions) {
                handleUpgradeClick();
                return;
              }
              addExceptionAction(
                'CURRENT_RETURN',
                [record],
                setSelectedRowKeys,
                allRowsSelected,
                setAllRowsSelected
              );
              onClose?.();
            }}
          >
            {lockExceptions && <LockFilled />}
            {t('exceptions.current-return')}
          </IconContextButton>
          <IconContextButton
            type="text"
            onClick={() => {
              if (lockExceptions) {
                handleUpgradeClick();
                return;
              }
              openAdjustmentPanal(
                [record],
                setSelectedRowKeys,
                allRowsSelected,
                setAllRowsSelected
              );
              onClose?.();
            }}
          >
            {lockExceptions && <LockFilled />}
            {t('exceptions.adjustment-return')}
          </IconContextButton>
          <IconContextButton
            type="text"
            onClick={() => {
              if (lockExceptions) {
                handleUpgradeClick();
                return;
              }
              addExceptionAction(
                'DEFER',
                [record],
                setSelectedRowKeys,
                allRowsSelected,
                setAllRowsSelected
              );
              onClose?.();
            }}
          >
            {lockExceptions && <LockFilled />}
            {t('exceptions.defer')}
          </IconContextButton>
        </Space>
      </div>
    );
  };

  const renderActionTag = (_, record: DataJournal) => {
    const action =
      record?.exceptionActionType != null
        ? ExceptionsActionType[record.exceptionActionType as keyof typeof ExceptionsActionType]
        : undefined;

    switch (action) {
      case ExceptionsActionType.UNRESOLVED:
      case ExceptionsActionType.DEFERRED_PREVIOUSLY:
        const prevDefer =
          action === ExceptionsActionType.DEFERRED_PREVIOUSLY;
        return (
          <ColoredTag
            color="var(--color-status-danger, #D13131)" //@ts-ignore
            bgcolor="var(--color-surface-raised, #FFFFFF)"
            bordered={true}
            bordercolor="var(--color-border, #dbeae3)"
          >
            {prevDefer ? (
              <Tooltip
                title={`Previously Deferred${
                  record?.deferredCount
                    ? ' ' +
                      record?.deferredCount +
                      (record?.deferredCount === 1 ? ' time' : ' times')
                    : ''
                }`}
              >
                {ExceptionsActionType.UNRESOLVED}{' '}
                {prevDefer
                  ? `(${record?.deferredCount ? record?.deferredCount : '*'})`
                  : ''}
              </Tooltip>
            ) : (
              <>{ExceptionsActionType.UNRESOLVED}</>
            )}
          </ColoredTag>
        );
      case ExceptionsActionType.ADJUSTMENT:
        return (
          <ColoredTag
            color="var(--color-status-info, #054581)" //@ts-ignore
            bgcolor="var(--color-status-info-bg, #e1edf6)"
            bordered={true}
            bordercolor="var(--color-border, #dbeae3)"
          >
            {ExceptionsActionType.ADJUSTMENT}
          </ColoredTag>
        );
      case ExceptionsActionType.CURRENT_RETURN:
        return (
          <ColoredTag
            color="var(--color-status-success, #058181)" //@ts-ignore
            bgcolor="var(--color-status-success-bg, #e9f6f7)"
            bordered={true}
            bordercolor="var(--color-border, #dbeae3)"
          >
            {ExceptionsActionType.CURRENT_RETURN}
          </ColoredTag>
        );
      case ExceptionsActionType.DEFER:
        return (
          <ColoredTag
            color="var(--color-status-warning, #c38a20)" //@ts-ignore
            bgcolor="var(--color-status-warning-bg, #fff5e2)"
            bordered={true}
            bordercolor="var(--color-border, #dbeae3)"
          >
            {ExceptionsActionType.DEFER}
          </ColoredTag>
        );

      default:
        return <>{record.exceptionActionType}</>;
    }
  };

  const getRowClassName = (record: DataJournal) =>
    isIgnoreTaxError(record) ? 'ignore-tax-warning-row' : '';

  // Memoize columns
  const columns = React.useMemo(() => {
    const baseColumns = [
      {
        title: '',
        dataIndex: 'providerIcon',
        key: 'providerIcon',
        fixed: 'left',
        render: (_: any, record: DataJournal) => {
          const hasIgnoreError = isIgnoreTaxError(record);
          const iconProvider =
            record.entityProviderName || providerName;
          const providerHref = getVatReportTransactionProviderLink(
            record,
            providerName,
            currentOrganisation?.organisationId,
            providerShortCode
          );
          const providerIcon = (
            <ProviderIcon
              providerName={iconProvider}
              alt={record.entityProviderName || providerName}
              style={{ marginTop: 7 }}
              width={25}
            />
          );

          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {providerHref ? (
                <a
                  href={providerHref}
                  style={{ cursor: 'pointer' }}
                  target="_blank"
                  rel="noreferrer"
                >
                  {providerIcon}
                </a>
              ) : (
                providerIcon
              )}
              {/* Always reserve space so logo aligns across all rows */}
              <span
                className="ignore-tax-warning-icon-slot"
                style={{
                  width: 24,
                  minWidth: 24,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {hasIgnoreError && (
                  <Tooltip title={t('exceptions.ignore-tax-warning-tooltip')}>
                    <span className="ignore-tax-warning-icon">
                      <WarningTwoTone
                        twoToneColor="#e99144"
                        style={{
                          fontSize: 18,
                          cursor: 'pointer',
                        }}
                      />
                    </span>
                  </Tooltip>
                )}
              </span>
            </div>
          );
        },
      },
      {
        title: t('vat-report.journal-date'),
        dataIndex: 'journalDate',
        key: 'journalDate',
        fixed: 'left',
        render: (_: any, record: DataJournal) => (
          <span>
            {formatDisplayDate(record.journalDate, 'DD/MM/YYYY', i18n.language)}
          </span>
        ),
        sorter: (a: DataJournal, b: DataJournal) =>
          new Date(a.journalDate).valueOf() - new Date(b.journalDate).valueOf(),
        sortIcon: renderSortIcon,
      },
    ];

    // Add the rest of the columns (Entity first for customize columns drawer, then Created Date, Tax Name, ...)
    let remainingColumns = [
      {
        title: t('common.entity'),
        dataIndex: 'entityName',
        key: 'entity',
        render: (_: any, record: DataJournal) => (
          <span>{record.entityName ?? ''}</span>
        ),
      },
      {
        title: t('vat-report.created-date'),
        dataIndex: 'journalCreatedDate',
        key: 'journalCreatedDate',
        render: (_: any, record: DataJournal) => (
          <span>{formatDate(record.journalCreatedDate)}</span>
        ),
        sorter: (a: DataJournal, b: DataJournal) =>
          new Date(a.journalCreatedDate).valueOf() -
          new Date(b.journalCreatedDate).valueOf(),
        sortIcon: renderSortIcon,
      },
      {
        title: t('organisation.tax-name'),
        dataIndex: 'taxName',
        key: 'taxName',
        render: (_: any, record: DataJournal) => (
          <span>{record.taxName || ''}</span>
        ),
        sorter: (a: DataJournal, b: DataJournal) =>
          (a.taxName || '').localeCompare(b.taxName || ''),
        sortIcon: renderSortIcon,
      },
      {
        title: t('exceptions.sourceType'),
        dataIndex: 'sourceType',
        key: 'sourceType',
        render: (content: string) => {
          return providerName?.toLowerCase() === 'xero'
            ? XeroSourceType[content]
            : providerName?.toLowerCase() === 'quickbooks'
            ? QboSourceType[content]
            : ZohoSourceType[content];
        },
        sorter: (a: DataJournal, b: DataJournal) =>
          (a.sourceType || '').localeCompare(b.sourceType || ''),
        sortIcon: renderSortIcon,
      },
      // A/C Code is hidden for Excel / Receipts providers (handled via columns
      // filtering below) so it never shows in the UI or customization.
      {
        title: t('transactions.accountCodeShort'),
        dataIndex: 'accountCode',
        key: 'accountCode',
        render: (_: any, record: DataJournal) => (
          <span>{record.accountCode || ''}</span>
        ),
        sorter: (a: DataJournal, b: DataJournal) =>
          (a.accountCode || '').localeCompare(b.accountCode || ''),
        sortIcon: renderSortIcon,
      },
      {
        title: t('transactions.accountType'),
        dataIndex: 'accountType',
        key: 'accountType',
        render: (_: any, record: DataJournal) => (
          <span>{record.accountType || ''}</span>
        ),
        sorter: (a: DataJournal, b: DataJournal) =>
          (a.accountType || '').localeCompare(b.accountType || ''),
        sortIcon: renderSortIcon,
      },
      {
        title: t('transactions.accountName'),
        dataIndex: 'accountName',
        key: 'accountName',
        render: (_: any, record: DataJournal) => (
          <span>{record.accountName || ''}</span>
        ),
        sorter: (a: DataJournal, b: DataJournal) =>
          (a.accountName || '').localeCompare(b.accountName || ''),
        sortIcon: renderSortIcon,
      },
      {
        title: t('transactions.contact'),
        dataIndex: 'contact',
        key: 'contact',
        render: (_: any, record: DataJournal) => (
          <span>{record.contact || ''}</span>
        ),
      },
      {
        title: t('transactions.invoice-no'),
        dataIndex: 'invoiceNumber',
        key: 'invoiceNumber',
        render: (_: any, record: DataJournal) => (
          <span>{record.invoiceNumber || ''}</span>
        ),
      },
      {
        title: t('common.description'),
        dataIndex: 'description',
        key: 'description',
        render: (_: any, record: DataJournal) => (
          <span
            style={{
              fontFamily: 'inherit',
              fontSize: 'inherit',
              fontWeight: 'inherit',
            }}
          >
            {record.description || ''}
          </span>
        ),
      },
      {
        title: t('organisation.reference'),
        dataIndex: 'reference',
        key: 'reference',
        render: (_: any, record: DataJournal) => (
          <span>{record.reference || ''}</span>
        ),
      },
      {
        title: t('common.currency'),
        dataIndex: 'currency',
        key: 'currency',
        render: (_: any, record: DataJournal) => (
          <span>{record.currency || ''}</span>
        ),
      },
      {
        title: t('exceptions.netAmount'),
        dataIndex: 'netAmountConverted',
        key: 'netAmountConverted',
        className: 'transactions-amount-column',
        render: (content: number) => (
          <div
            style={{
              textAlign: 'end',
              color: content < 0 ? '#d13131' : '',
            }}
          >
            {formatNumber(content)}
          </div>
        ),
        sorter: (a: DataJournal, b: DataJournal) =>
          a.netAmountConverted > b.netAmountConverted ? -1 : 1,
        sortIcon: renderSortIcon,
      },
      {
        title: t('transactions.taxAmount'),
        dataIndex: 'taxAmountConverted',
        key: 'taxAmountConverted',
        className: 'transactions-amount-column',
        render: (content: number) => (
          <div
            style={{ textAlign: 'end', color: content < 0 ? '#d13131' : '' }}
          >
            {formatNumber(content)}
          </div>
        ),
        sorter: (a: DataJournal, b: DataJournal) =>
          a.taxAmountConverted > b.taxAmountConverted ? -1 : 1,
        sortIcon: renderSortIcon,
      },
      {
        title: t('main.action-required'),
        dataIndex: 'exceptionActionType',
        key: 'exceptionActionType',
        fixed: 'right',
        render: renderActionTag,
        sorter: (a: DataJournal, b: DataJournal) =>
          (a.exceptionActionType || '').localeCompare(
            b.exceptionActionType || ''
          ),
        sortIcon: renderSortIcon,
      },
      !handleExceptions
        ? {
            title: '',
            dataIndex: 'emptyRight',
            key: 'emptyRight',
            fixed: 'right',
            render: () => <span />,
          }
        : {
            title: '',
            dataIndex: 'contextMenu',
            key: 'contextMenu',
            fixed: 'right',
            render: (_: any, record: DataJournal) => {
              const isDisabled = isIgnoreTaxError(record);
              if (isDisabled) {
                return (
                  <ContextButton
                    type="text"
                    disabled
                    aria-label={t('common.more-actions')}
                    style={{ cursor: 'not-allowed', opacity: 0.5 }}
                  >
                    <EllipseIcon
                      style={{
                        width: 18,
                        height: 18,
                      }}
                    />
                  </ContextButton>
                );
              }
              return (
                <Popover
                  placement="topLeft"
                  arrow={false}
                  trigger="click"
                  content={renderContextMenu(record, () =>
                    setContextMenuVisible((prev) => ({
                      ...prev,
                      [record.uuid]: false,
                    }))
                  )}
                  title={''}
                  overlayStyle={{ zIndex: 500 }}
                  open={contextMenuVisible[record.uuid]}
                  destroyOnHidden={true}
                  onOpenChange={(visible) =>
                    setContextMenuVisible((prev) => ({
                      ...prev,
                      [record.uuid]: visible,
                    }))
                  }
                >
                  <ContextButton type="text" aria-label={t('common.more-actions')}>
                    <EllipseIcon
                      style={{
                        width: 18,
                        height: 18,
                      }}
                    />
                  </ContextButton>
                </Popover>
              );
            },
          },
    ];

    const providerLower = providerName?.toLowerCase();
    if (providerLower === 'zoho') {
      remainingColumns = remainingColumns.filter(
        (col) =>
          ![
            'accountCode',
            'accountType',
            'accountName',
            'description',
            'reference',
          ].includes(col.key)
      );
    }

    // For Excel / Receipts providers, completely remove the A/C Code column so
    // it can't be displayed or customized.
    if (providerLower === 'excel' || providerLower === 'receipts') {
      remainingColumns = remainingColumns.filter(
        (col) => col.key !== 'accountCode'
      );
    }

    return [...baseColumns, ...remainingColumns];
  }, [
    providerName,
    customPagination,
    renderSortIcon,
    t,
    handleExceptions,
    currentOrganisation?.organisationId,
    providerShortCode,
  ]);

  // Match Transactions: strip Ant sticky columns on phone (cards + pager only).
  const pwaSafeExceptionColumns = React.useMemo(() => {
    if (!pwaMobileNav) {
      return columns;
    }
    return columns.map((col) => {
      if (!col || !('fixed' in col) || (col as { fixed?: unknown }).fixed == null) {
        return col;
      }
      const { fixed: _fixed, ...rest } = col as typeof col & {
        fixed?: 'left' | 'right' | boolean;
      };
      return rest;
    });
  }, [pwaMobileNav, columns]);

  const openExceptionsColumnsDrawer = React.useCallback(() => {
    const customizableColumns =
      columns
        ?.filter(
          (col) =>
            col.key && !col.fixed && !EXCEPTIONS_ALWAYS_FIXED.includes(col.key)
        )
        .map((col) => ({
          key: col.key as string,
          title: col.title,
          fixed: col.fixed as boolean | 'left' | 'right' | undefined,
        })) || [];

    const defaultVisible =
      visibleColumns ||
      columns?.filter((col) => col.key && !col.fixed).map((col) => col.key) ||
      [];

    const drawerPayload: DrawerData = {
      title: t('main.columns', 'Columns'),
      type: DrawerType.CUSTOMIZE_COLUMNS,
      component: (
        <ColumnCustomizationDrawer
          columns={customizableColumns}
          visibleColumns={defaultVisible}
          lockedColumns={lockedColumns}
          columnOrder={columnOrder}
          alwaysFixedColumns={EXCEPTIONS_ALWAYS_FIXED}
          onSave={handleColumnsChange}
          onClose={() => dispatch(turnDrawerOff())}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerPayload));
  }, [
    columns,
    columnOrder,
    dispatch,
    handleColumnsChange,
    lockedColumns,
    t,
    visibleColumns,
  ]);

  const search = (e) => {
    if (!customPagination) {
      const value = e.target.value;
      if (value != '') {
        const filterTable = transData?.filter((o) =>
          Object.keys(o).some((k) =>
            String(o[k]).toLowerCase().includes(value.toLowerCase())
          )
        );

        setSource(filterTable);
      } else {
        setSource(transData);
      }
    } else {
      setSearchFilterValue(e.target.value);
    }
  };

  const rowSelection: TableProps<DataJournal>['rowSelection'] = {
    selectedRowKeys: selectedRowKeys.map((sr) => sr.key),
    onChange: (_, selectedRows: DataJournal[], info) => {
      if (info?.type === 'all' || info?.type === 'none') {
        return;
      }
      setAllRowsSelected(false);
      setSelectedRowKeys(selectedRows);
    },
    columnTitle: (originalNode) =>
      React.isValidElement(originalNode)
        ? React.cloneElement(
            originalNode as React.ReactElement<{ 'aria-label'?: string }>,
            { 'aria-label': t('common.select-all') }
          )
        : originalNode,
    getCheckboxProps: (record: DataJournal) => ({
      name: record.accountName,
      disabled: isIgnoreTaxError(record),
      'aria-label':
        record.invoiceNumber ||
        record.description ||
        record.accountName ||
        record.taxName ||
        t('vat-report.exceptions'),
    }),
  };

  const exportToCsv = () => {
    dispatch(exportToCsvRequest(reportId, JournalType.EXCEPTION, 'detailed'));
  };

  const renderCardTitle = () => {
    return (
      <Space
        className={pwaMobileNav ? 'scope-pwa-vat-transactions-header' : undefined}
        style={{
          display: 'flex',
          marginBottom: 20,
          justifyContent: 'space-between',
        }}
      >
        <Space
          className={
            pwaMobileNav ? 'scope-pwa-vat-transactions-header__left' : undefined
          }
        >
          {!pwaMobileNav ? (
            <ExportButton
              icon={<IconExport style={{ width: 20, height: 20 }} />}
              onClick={exportToCsv}
              loading={csvLoading}
            >
              <span>
                {t('common.export')} {t('vat-report.exceptions')}
              </span>
            </ExportButton>
          ) : null}
        </Space>

        <RoundInput
          className={
            pwaMobileNav ? 'scope-pwa-vat-transactions-header__search' : undefined
          }
          width={pwaMobileNav ? '100%' : '200px'}
          height={'35px'}
          prefix={<SearchOutlined />}
          size="large"
          placeholder={
            pwaMobileNav
              ? t('vat-report.search-contact-or-description', {
                  defaultValue: 'Search contact or description',
                })
              : t('exceptions.search-exceptions')
          }
          onChange={search}
        />
      </Space>
    );
  };

  const calcAmount = (amountType) => {
    if (amountType == 'net') {
      const netAmountDataSource: DataJournal[] | undefined =
        selectedRowKeys.length > 0 ? selectedRowKeys : source;

      return netAmountDataSource?.reduce(
        (exception, { netAmountConverted }) => exception + netAmountConverted,
        0
      );
    } else if (amountType == 'tax') {
      const taxAmountDataSource: DataJournal[] | undefined =
        selectedRowKeys.length > 0 ? selectedRowKeys : source;

      return taxAmountDataSource?.reduce(
        (exception, { taxAmountConverted }) => exception + taxAmountConverted,
        0
      );
    }
  };

  const clearSelection = () => {
    setSelectedRowKeys([]);
    setAllRowsSelected(false);
  };

  const runBulkAction = (action: () => void) => {
    if (lockExceptions) {
      handleUpgradeClick();
      return;
    }
    action();
  };

  const selectionActionButtonProps = {
    size: 'small' as const,
    fontSize: '12px',
    lineheight: '16px',
    height: '32px',
    width: 'fit-content',
    style: {
      paddingLeft: 12,
      paddingRight: 12,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
    },
  };

  const renderSelectionBar = () => {
    if (
      !canApplyExceptionActions ||
      (selectedRowKeys.length === 0 && !allRowsSelected)
    ) {
      return null;
    }

    const selectedCount = allRowsSelected
      ? total ?? selectedRowKeys.length
      : selectedRowKeys.length;
    const totalCount = total ?? source?.length ?? 0;
    const netAmount = calcAmount('net');
    const taxAmount = calcAmount('tax');

    return (
      <ExceptionsSelectionBar>
        <Space size={12} wrap align="center" style={{ flex: 1, minWidth: 0 }}>
          <SelectionBarStat>
            <b>
              {allRowsSelected
                ? t('exceptions.all-selected', { count: selectedCount })
                : t('exceptions.selected-count', {
                    count: selectedCount,
                    total: totalCount,
                  })}
            </b>
          </SelectionBarStat>
          <SelectionBarDivider />
          <SelectionBarStat>
            {t('exceptions.net-amount-lowercase')}:{' '}
            <b>{netAmount != null ? formatNumber(netAmount) : null}</b>
          </SelectionBarStat>
          <SelectionBarDivider />
          <SelectionBarStat>
            {t('exceptions.tax-amount-lowercase')}:{' '}
            <b>{taxAmount != null ? formatNumber(taxAmount) : null}</b>
          </SelectionBarStat>
        </Space>
        <Space size={8} wrap>
          <ScopeButton
            type="dark-orange"
            {...selectionActionButtonProps}
            title={t('exceptions.current-return')}
            onClick={() =>
              runBulkAction(() =>
                addExceptionAction(
                  'CURRENT_RETURN',
                  selectedRowKeys,
                  setSelectedRowKeys,
                  allRowsSelected,
                  setAllRowsSelected
                )
              )
            }
          >
            {lockExceptions && <LockFilled />}
            {t('exceptions.include-in-submission')}
          </ScopeButton>
          <ScopeButton
            type="default"
            {...selectionActionButtonProps}
            title={t('exceptions.adjustment-return')}
            onClick={() =>
              runBulkAction(() =>
                openAdjustmentPanal(
                  selectedRowKeys,
                  setSelectedRowKeys,
                  allRowsSelected,
                  setAllRowsSelected
                )
              )
            }
          >
            {lockExceptions && <LockFilled />}
            {t('exceptions.adjust-to-prior')}
          </ScopeButton>
          <ScopeButton
            type="default"
            {...selectionActionButtonProps}
            title={t('exceptions.defer')}
            onClick={() =>
              runBulkAction(() =>
                addExceptionAction(
                  'DEFER',
                  selectedRowKeys,
                  setSelectedRowKeys,
                  allRowsSelected,
                  setAllRowsSelected
                )
              )
            }
          >
            {lockExceptions && <LockFilled />}
            {t('exceptions.defer-to-next')}
          </ScopeButton>
          <ScopeButton
            type="default"
            {...selectionActionButtonProps}
            onClick={clearSelection}
          >
            {t('exceptions.deselect')}
          </ScopeButton>
        </Space>
      </ExceptionsSelectionBar>
    );
  };

  const renderItemsSelected = () => {
    const totalItems = source ? source.length : 0;
    return `${totalItems} items | `;
  };

  const renderHeaderComponent = () => {
    return (
      <Space direction="horizontal" style={{ float: 'right' }}>
        <CardDescSpan
          style={{
            float: 'right',
            fontSize: 11,
          }}
        >
          {renderItemsSelected()}
          {t('vat-forms.totals')}:{' '}
          {t('organisation.extraction-rules.fields.net')}:{' '}
          <span
            style={{
              textAlign: 'end',
              fontWeight: 600,
            }}
          >
            {calcAmount('net') != null ? formatNumber(calcAmount('net')) : null}
          </span>
          {'     '}
          {t('organisation.extraction-rules.fields.tax')}:{' '}
          <span
            style={{
              textAlign: 'end',
              fontWeight: 600,
            }}
          >
            {calcAmount('tax') != null ? formatNumber(calcAmount('tax')) : null}
          </span>
        </CardDescSpan>
      </Space>
    );
  };
  const savePageSize = (pageSize: number) => {
    const preferenceSection = PreferenceSections.EXCEPTIONS;
    handlePreferences(preferenceSection, PreferenceActions.SET, {
      name: PAGE_SIZE_EXCEPTIONS,
      value: pageSize,
    });
  };
  const onSelectAll = (selected: boolean) => {
    if (!selected) {
      clearSelection();
      return;
    }

    const selectableRows = getSelectableRows(source);
    if (!tableHasFiltersApplied) {
      setAllRowsSelected(true);
    } else {
      setAllRowsSelected(false);
    }
    setSelectedRowKeys(selectableRows);
  };

  const EXCEPTIONS_VISIBLE_FILTERS = 'exceptions-visible-filters';

  const getFiltersList = () => {
    const provider = providerName?.toLowerCase();

    if (provider === 'zoho') {
      return [
        'entity',
        'taxName',
        'selectedValue',
        'journalDate',
        'journalCreatedDate',
        'sourceType',
        'netAmountConverted',
        'exceptionActionType',
      ];
    }

    // For Excel / Receipts providers we hide "accountCode" entirely (no filter).
    if (provider === 'excel' || provider === 'receipts') {
      return [
        'entity',
        'taxName',
        'selectedValue',
        'journalDate',
        'journalCreatedDate',
        'sourceType',
        'accountType',
        'accountName',
        'netAmountConverted',
        'exceptionActionType',
      ];
    }

    return [
      'entity',
      'taxName',
      'selectedValue',
      'journalDate',
      'journalCreatedDate',
      'sourceType',
      'accountCode',
      'accountType',
      'accountName',
      'netAmountConverted',
      'exceptionActionType',
    ];
  };

  // Default visible filters: Tax Name, Created Date, A/c name, net amount, Action (Entity and Mapping off by default)
  const DEFAULT_VISIBLE_FILTERS_EXCEPTIONS = [
    'taxName',
    'journalCreatedDate',
    'accountName',
    'netAmountConverted',
    'exceptionActionType',
  ];
  // Phone: keep the filter strip lean; users add more via the + drawer.
  const DEFAULT_VISIBLE_FILTERS_EXCEPTIONS_PWA = [
    'journalDate',
    'exceptionActionType',
  ];

  const allFilters = React.useMemo(() => getFiltersList(), [providerName]);
  const savedVisibleFiltersExceptions = handlePreferences(
    PreferenceSections.EXCEPTIONS,
    PreferenceActions.GET,
    { name: EXCEPTIONS_VISIBLE_FILTERS }
  );
  const [visibleFilters, setVisibleFilters] = React.useState<string[]>(() => {
    if (pwaMobileNav) {
      return DEFAULT_VISIBLE_FILTERS_EXCEPTIONS_PWA.filter((f) =>
        allFilters.includes(f)
      );
    }
    if (
      savedVisibleFiltersExceptions &&
      Array.isArray(savedVisibleFiltersExceptions) &&
      savedVisibleFiltersExceptions.length > 0
    ) {
      const valid = savedVisibleFiltersExceptions.filter((f) =>
        allFilters.includes(f)
      );
      if (valid.length > 0) return valid;
    }
    return DEFAULT_VISIBLE_FILTERS_EXCEPTIONS.filter((f) =>
      allFilters.includes(f)
    );
  });

  const handleOpenFilterSelection = React.useCallback(() => {
    const drawerData = {
      title: t('submissions.select-filters-drawer-title'),
      subtitle: t('submissions.select-filters-subtitle'),
      type: DrawerType.FILTER_SELECTION,
      component: (
        <FilterSelectionDrawer
          availableFilters={allFilters}
          selectedFilters={visibleFilters}
          onUpdate={(selectedFilters) => {
            setVisibleFilters(selectedFilters);
            handlePreferences(
              PreferenceSections.EXCEPTIONS,
              PreferenceActions.SET,
              {
                name: EXCEPTIONS_VISIBLE_FILTERS,
                value: selectedFilters,
              }
            );
          }}
          onClose={() => dispatch(turnDrawerOff())}
          skipPreferenceSave
          mainLocaleKey="exceptions"
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  }, [allFilters, visibleFilters, handlePreferences, dispatch]);

  React.useEffect(() => {
    setVisibleFilters((prev) => {
      const valid = prev.filter((f) => allFilters.includes(f));
      if (valid.length === prev.length) return prev;
      const fallback = pwaMobileNav
        ? DEFAULT_VISIBLE_FILTERS_EXCEPTIONS_PWA
        : DEFAULT_VISIBLE_FILTERS_EXCEPTIONS;
      return valid.length > 0
        ? valid
        : fallback.filter((f) => allFilters.includes(f));
    });
  }, [allFilters, pwaMobileNav]);

  React.useEffect(() => {
    // Check if we should show the warning panel
    const shouldShowWarning =
      status &&
      (DataLogStatus[status] === DataLogStatus.SUBMITTED ||
        DataLogStatus[status] === DataLogStatus.CANCELLED) &&
      (!transData || transData.length === 0) &&
      (!vatReportData?.journals || vatReportData.journals.length === 0) &&
      !sessionStorage.getItem('just-submitted');

    if (!shouldShowWarning) {
      setLoadTransactionsClicked(true);
    }
  }, [transData, vatReportData]);

  const renderSubmittedWarning = () => {
    // Show warning if status is SUBMITTED or CANCELLED and there are no exceptions
    const isSubmittedOrCancelled =
      status &&
      (DataLogStatus[status] === DataLogStatus.SUBMITTED ||
        DataLogStatus[status] === DataLogStatus.CANCELLED);
    const hasExceptions = transData && transData.length > 0;

    if (!isSubmittedOrCancelled || hasExceptions) {
      return null;
    }

    return (
      <StyledCard
        $nopadding
        fullsize
        style={{
          boxShadow: '0px 4px 10px 0px #0000000D',
          marginBottom: '20px',
          display: 'flex',
          fontSize: 14,
          fontWeight: 400,
          padding: 0,
          maxWidth: 1150,
          marginLeft: 'auto',
          marginRight: 'auto',
          width: '100%',
        }}
        width="100%"
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexFlow: 'row',
            alignContent: 'end',
            padding: '24px 24px 0 0',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexFlow: 'column',
              justifyContent: 'space-between',
              padding: '0 24px 24px 24px',
            }}
          >
            <Space>
              <CardTitleLargeSpan style={{ marginBottom: 10 }}>
                {t('vat-report.download-exceptions-title')}
              </CardTitleLargeSpan>
              <ExclamationCircleOutlined
                style={{ color: '#D13131', fontSize: 18 }}
              />
            </Space>
            <CardDescSpan style={{ marginBottom: 16 }}>
              {t('vat-report.download-transactions-description')}
            </CardDescSpan>
            <ScopeButton
              type="primary"
              width="fit-content"
              height="40px"
              style={{ fontSize: '13px', fontWeight: 700 }}
              onClick={onLoadReport}
              disabled={!onLoadReport}
            >
              {t('vat-report.download-exceptions-button')}
            </ScopeButton>
          </div>

          <img alt={t('vat-report.vat-checker-image-alt')} src={GenTaxImage} />
        </div>
      </StyledCard>
    );
  };

  // If warning should be shown, only render the warning panel
  if (!loadTransactionsClicked) {
    return <>{renderSubmittedWarning()}</>;
  }

  return (
    <>
      <ExceptionsWrapper
        className={pwaMobileNav ? 'scope-pwa-vat-exceptions' : undefined}
      >
        {handleExceptions && !pwaMobileNav && (
          <>
            {showCard ? (
              <StyledCard
                width="100%"
                centercontent
                hideheader
                style={{
                  marginBottom: '20px',
                  maxWidth: 1300,
                  //minWidth: 1100,
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  width: '100%',
                  //padding: '0 24px !important',
                }}
                extra={
                  <IconClose
                    role="button"
                    tabIndex={0}
                    aria-label={t('common.close')}
                    style={{
                      width: 20,
                      height: 20,
                      top: '25',
                      position: 'absolute',
                      right: '25',
                      cursor: 'pointer',
                      filter:
                        'invert(71%) sepia(12%) saturate(247%) hue-rotate(131deg) brightness(80%) contrast(81%)',
                    }}
                    onClick={() => {
                      setShowCard(false);
                      handlePreferences(
                        PreferenceSections.EXCEPTIONS,
                        PreferenceActions.SET,
                        {
                          name: EXCEPTIONS_TUTORIAL_HIDE,
                          value: true,
                        }
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowCard(false);
                        handlePreferences(
                          PreferenceSections.EXCEPTIONS,
                          PreferenceActions.SET,
                          {
                            name: EXCEPTIONS_TUTORIAL_HIDE,
                            value: true,
                          }
                        );
                      }
                    }}
                  />
                }
              >
                <Space
                  style={{
                    columnGap: 50,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    display: 'flex',
                    width: '98%',
                  }}
                  align="start"
                >
                  <Space
                    direction="vertical"
                    style={{ maxWidth: '270px', marginRight: '20px' }}
                  >
                    <CardTitleLargeSpan>
                      {t('vat-report.process-exceptions')}
                    </CardTitleLargeSpan>
                    <CardDescSpan>
                      {t(
                        'vat-report.flagged-exceptions-reviewed-before-submitted'
                      )}
                    </CardDescSpan>
                  </Space>
                  <Space direction="vertical">
                    <img
                      alt="exception-step-1"
                      style={{
                        width: '100%',
                        height: '230px',
                        maxWidth: '290px',
                        minWidth: '250px',
                      }}
                      src={ExceptionStepOne}
                    />
                  </Space>
                  <Space direction="vertical">
                    <img
                      alt="exception-step-2"
                      style={{
                        width: '100%',
                        height: '230px',
                        maxWidth: '290px',
                        minWidth: '250px',
                      }}
                      src={ExceptionStepTwo}
                    />
                  </Space>
                </Space>
              </StyledCard>
            ) : (
              <Row justify={'end'} style={{ marginTop: -20, marginBottom: 20 }}>
                <ExportButton
                  type="text"
                  onClick={() => {
                    setShowCard(true);
                    handlePreferences(
                      PreferenceSections.EXCEPTIONS,
                      PreferenceActions.SET,
                      {
                        name: EXCEPTIONS_TUTORIAL_HIDE,
                        value: false,
                      }
                    );
                  }}
                  icon={<IconSupport style={{ width: 20, height: 20 }} />} //@ts-ignore
                  isGuide
                >
                  Open Guide
                </ExportButton>
              </Row>
            )}
          </>
        )}
        <StyledCard //@ts-ignore
          className={
            pwaMobileNav ? 'scope-pwa-vat-transactions-card-shell' : undefined
          }
          style={{
            width: '100%',
            maxWidth: '100%',
            margin: 'auto',
          }}
        >
          {((!customPagination && transData && transData?.length > 0) ||
            customPagination) &&
            renderCardTitle()}
          <Divider />
          <div
            className={
              pwaMobileNav ? 'scope-pwa-vat-transactions-body' : undefined
            }
            style={{
              padding: pwaMobileNav ? undefined : '10px',
              paddingLeft: pwaMobileNav ? undefined : '5px',
              paddingTop: pwaMobileNav ? undefined : '10px',
              //maxWidth: 1600,
              //minWidth: 800,
              margin: 'auto',
            }}
          >
            <TransactionTableWrapper>
            <div
              className={
                pwaMobileNav ? 'scope-pwa-entities-filters-row' : undefined
              }
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 16,
              }}
            >
              <div
                className={
                  pwaMobileNav ? 'scope-pwa-entities-filters' : undefined
                }
                style={{ flex: 1, minWidth: 0 }}
              >
                <ScopeTableFilters
                  filters={allFilters}
                  visibleFilters={visibleFilters}
                  mainLocaleKey="exceptions"
                  onOpenFilterSelection={handleOpenFilterSelection}
                  value={tableFiltersState}
                  onChange={setTableFiltersState}
                  originalDataSource={source}
                  tableDataSource={source}
                  selectedFilter={{
                    key: 'selectedValue',
                    value: filteredBox ?? '',
                  }}
                  customPagination={customPagination}
                  onServerFilter={handleServerFilter}
                  setHasFilters={setTableHasFiltersApplied}
                  embedded={false}
                  filterRowMarginBottom={showSideMenu ? 0 : 6}
                  matchTriggerWidth={pwaMobileNav}
                  footerRightContent={
                    !pwaMobileNav && source && source.length > 0 ? (
                      <CustomiseColumnsIconButtonWithTooltip
                        type="default"
                        style={customiseColumnsIconButtonStyle(
                          exceptionsColumnsDrawerOpen
                        )}
                        icon={
                          <SettingOutlined
                            style={{ fontSize: CUSTOMISE_COLUMNS_ICON_FONT_SIZE }}
                          />
                        }
                        onClick={() => openExceptionsColumnsDrawer()}
                      />
                    ) : undefined
                  }
                />
              </div>
              {!pwaMobileNav &&
                (handleExceptions || (source && source.length > 0)) && (
                <div
                  style={{
                    flexShrink: 0,
                    flexBasis: showSideMenu ? '100%' : undefined,
                    justifyContent: 'flex-end',
                    marginLeft: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: showSideMenu ? 0 : 5,
                  }}
                >
                  <CustomiseColumnsIconButtonWithTooltip
                    type="default"
                    style={customiseColumnsIconButtonStyle(
                      exceptionsColumnsDrawerOpen
                    )}
                    icon={
                      <SettingOutlined
                        style={{ fontSize: CUSTOMISE_COLUMNS_ICON_FONT_SIZE }}
                      />
                    }
                    onClick={() => openExceptionsColumnsDrawer()}
                  />
                </div>
              )}
            </div>
            {pwaMobileNav ? null : renderSelectionBar()}
            {pwaMobileNav ? (
              <PwaVatTransactionsCardList
                rows={source || []}
                emptyMessage={t('global.no-data-available')}
                emptyTitle={t('vat-report.no-exceptions', {
                  defaultValue: 'No exceptions',
                })}
                emptyDescription={t('vat-report.no-exceptions-description', {
                  defaultValue:
                    'Every transaction in this period mapped to a VAT box. Exceptions appear here when one needs your attention.',
                })}
                formatAmount={formatCardAmount}
                onOpenDetails={setSelectedPwaException}
              />
            ) : null}
            <div
              className={
                pwaMobileNav
                  ? 'scope-pwa-vat-transactions-table-scroll scope-pwa-vat-transactions-table-scroll--pager-only'
                  : undefined
              }
            >
              <ScopeTable
                className="transaction-table"
                defaultSort={['journalDate', 'desc']}
                externalFiltersState={tableFiltersState}
                rowClassName={getRowClassName}
                rowSelection={
                  canApplyExceptionActions && !pwaMobileNav
                    ? {
                        type: 'checkbox',
                        onSelectAll: onSelectAll,
                        selectedRowKeys: selectedRowKeys.map((sr) => sr.key),
                        ...rowSelection,
                      }
                    : undefined
                }
                mainLocaleKey="exceptions"
                tableDataSource={source}
                originalDataSource={source}
                columns={pwaSafeExceptionColumns}
                pagination={true}
                rowsPerPage={getSavedPageSize()}
                enableColumnsCustomization={!pwaMobileNav}
                hideToolbarColumnsCustomizationButton
                onColumnsChange={handleColumnsChange}
                visibleColumns={
                  pwaMobileNav
                    ? Array.from(
                        new Set([
                          'providerIcon',
                          'journalDate',
                          ...(visibleColumns || defaultVisibleColumnsExceptions),
                          ...EXCEPTIONS_ALWAYS_FIXED,
                          handleExceptions ? 'contextMenu' : 'emptyRight',
                        ])
                      )
                    : visibleColumns || undefined
                }
                lockedColumns={pwaMobileNav ? [] : lockedColumns}
                columnOrder={
                  pwaMobileNav
                    ? [
                        'providerIcon',
                        'journalDate',
                        ...(columnOrder || []).filter(
                          (key) =>
                            key !== 'pwaDetail' &&
                            key !== 'providerIcon' &&
                            key !== 'journalDate'
                        ),
                      ]
                    : columnOrder
                }
                alwaysFixedColumns={
                  pwaMobileNav ? [] : EXCEPTIONS_ALWAYS_FIXED
                }
                rowHeight={pwaMobileNav ? 42 : 40} //@ts-ignore
                titleComponent={
                  handleExceptions && !pwaMobileNav && selectedRowKeys.length === 0
                    ? renderHeaderComponent()
                    : undefined
                }
                showSizeChanger={!pwaMobileNav}
                getNewPageSize={savePageSize}
                scroll={
                  pwaMobileNav
                    ? undefined
                    : {
                        x: 'max-content',
                      }
                }
                fontSize={12}
                transactionType={JournalType.EXCEPTION}
                total={total}
                reportId={reportId}
                customPagination={customPagination}
                journalType={'detailed'}
                searchFilterValue={searchFilterValue}
                headerFontSize={12}
              />
            </div>
            {pwaMobileNav && selectedPwaException ? (
              <PwaVatTxnDetailSheet
                record={selectedPwaException}
                onClose={closePwaExceptionDetail}
                formatAmount={formatCardAmount}
                formatText={formatCardText}
                formatDate={formatDate}
                dragZoneRef={pwaDetailDragZoneRef}
                dragCloseHandlers={pwaDetailDragClose}
              />
            ) : null}
            </TransactionTableWrapper>          </div>
        </StyledCard>
      </ExceptionsWrapper>
    </>
  );
};
